import _, { cloneDeep } from 'lodash'
import moment from 'moment'
import { TYPE } from '~/constants/EditFormConstants'
import { APPROVAL_ACTION_TYPE, WF_STEP_PROCESSING_TYPE } from '~/types/workflow'
import { findWhere } from 'underscore'

const WF_TPL_STEP = {
  initialApprovers: 'initialApprovers',
  collaborators: 'collaborators',
  reviewers: 'reviewers',
  approvers: 'approvers',
  rejectionReviewers: 'rejectionReviewers',
  trainees: 'trainees'
}

export const transformToServer = (tpl: any) => {
  const transformedTpl = cloneDeep(tpl)

  _.forEach(
    [WF_TPL_STEP.initialApprovers, WF_TPL_STEP.collaborators, WF_TPL_STEP.reviewers, WF_TPL_STEP.approvers],
    (key) => {
      _.forEach(transformedTpl[key]?.steps, (step) => {
        step.taskTeamStepLabel = step.taskTeamStepLabel
          ? { id: step.taskTeamStepLabel.value || step.taskTeamStepLabel.id }
          : null

        step.taskTeams = []

        _.forEach(step.usersGroups, (stepItem) => {
          const stepItemArr = stepItem.split('-')
          step.taskTeams.push(
            stepItemArr[0] === 'GROUP'
              ? {
                  group: { id: stepItemArr[1] }
                }
              : {
                  user: { id: stepItemArr[1] }
                }
          )
        })

        step.usersGroups = undefined
      })
    }
  )

  _.forEach(transformedTpl[WF_TPL_STEP.approvers]?.approveActions, (action) => {
    if (!action.actionType) {
      return
    }
    action['@type'] = action.actionType
    action.notifies = []

    switch (action.actionType) {
      case APPROVAL_ACTION_TYPE.SET_PROPERTY:
        const newActionData: { metadataTemplateId: any; value?: any }[] = []

        _.forEach(action.actionData, (propActionRow) => {
          // Skip rows without property set (incomplete/empty rows)
          if (!propActionRow.property) {
            return
          }

          const actionDataRow: { metadataTemplateId: any; value?: any } = {
            metadataTemplateId: propActionRow.metadataTemplateId
          }

          if (propActionRow.property.type === TYPE.DATETIME && propActionRow.val && propActionRow.val.unix) {
            actionDataRow.value = propActionRow.val.unix()
          } else if ([TYPE.SELECT_ONE_USER, TYPE.SELECT_ONE_GROUP].indexOf(propActionRow.property.type) > -1) {
            const userSelectionObj = propActionRow.val.split('-')
            actionDataRow.value = JSON.stringify({ id: userSelectionObj[1], systemHelperType: userSelectionObj[0] })
          } else if ([TYPE.SELECT_MULTI_GROUP, TYPE.SELECT_MULTI_USER].indexOf(propActionRow.property.type) > -1) {
            const actionDataRowValue = _.map(propActionRow.val, (v) => {
              const userSelectionObj = v.split('-')
              return { id: userSelectionObj[1], systemHelperType: userSelectionObj[0] }
            })
            actionDataRow.value = JSON.stringify(actionDataRowValue)
          } else {
            actionDataRow.value = JSON.stringify(propActionRow.val)
          }

          newActionData.push(actionDataRow)
        })

        action.actionData = newActionData
        break
      case APPROVAL_ACTION_TYPE.COPY:
      case APPROVAL_ACTION_TYPE.MOVE:
        action.actionData = action.actionData.id
        break
    }

    _.forEach(action.usersGroups, (stepItem) => {
      const stepItemArr = stepItem.split('-')
      action.notifies.push(
        stepItemArr[0] === 'GROUP'
          ? {
              group: { id: stepItemArr[1] }
            }
          : {
              user: { id: stepItemArr[1] }
            }
      )
    })

    action.usersGroups = undefined
    action.actionType = undefined
  })

  _.forEach([WF_TPL_STEP.trainees, WF_TPL_STEP.rejectionReviewers], (key) => {
    if (transformedTpl[key]) {
      transformedTpl[key].steps = transformedTpl[key].usersGroups.length
        ? [
            {
              processingType: WF_STEP_PROCESSING_TYPE.PARALLEL,
              taskTeams: []
            }
          ]
        : []

      _.forEach(transformedTpl[key].usersGroups, (stepItem) => {
        const stepItemArr = stepItem.split('-')
        transformedTpl[key].steps[0].taskTeams.push(
          stepItemArr[0] === 'GROUP'
            ? {
                group: { id: stepItemArr[1] }
              }
            : {
                user: { id: stepItemArr[1] }
              }
        )
      })
      transformedTpl[key].usersGroups = undefined
    }
  })

  return transformedTpl
}

export const transformFromServer = function (tpl: any, allMetadatas: any) {
  const transformedTpl = { ...tpl }

  _.forEach(
    [WF_TPL_STEP.initialApprovers, WF_TPL_STEP.collaborators, WF_TPL_STEP.reviewers, WF_TPL_STEP.approvers],
    (key) => {
      _.forEach(transformedTpl[key]?.steps, (step) => {
        step.usersGroups = []
        step.nofAllValue = step.nofAllValue || ''
        _.forEach(step.taskTeams, (stepItem) => {
          step.usersGroups.push(stepItem.group ? 'GROUP-' + stepItem.group.id : 'USER-' + stepItem.user.id)
        })

        step.taskTeams = undefined
      })
    }
  )

  _.forEach(transformedTpl[WF_TPL_STEP.approvers]?.approveActions, (action) => {
    action.actionType = action['@type']
    action.usersGroups = []

    switch (action.actionType) {
      case APPROVAL_ACTION_TYPE.SET_PROPERTY:
        _.forEach(action.actionData, (actionData) => {
          actionData.property = findWhere(allMetadatas, { id: actionData.metadataTemplateId })

          actionData.metadataTemplateType = actionData.property ? actionData.property.type : ''

          if (actionData.property && [TYPE.DATETIME].indexOf(actionData.property.type) > -1) {
            actionData.val = moment.unix(actionData.value)
          } else {
            actionData.val = JSON.parse(actionData.value)
          }

          if (actionData.property && [TYPE.SELECT_ONE, TYPE.SELECT_MULTI].indexOf(actionData.property.type) > -1) {
            actionData.property.definition.options = _.map(actionData.property.definition.options, (i) => {
              return { label: i, value: i }
            })
          }

          if (
            actionData.property &&
            [TYPE.SELECT_MULTI_USER, TYPE.SELECT_MULTI_GROUP].indexOf(actionData.property.type) > -1
          ) {
            actionData.val = _.map(actionData.val, (i) => {
              return i.systemHelperType + '-' + i.id
            })
          }
          if (
            actionData.property &&
            [TYPE.SELECT_ONE_USER, TYPE.SELECT_ONE_GROUP].indexOf(actionData.property.type) > -1
          ) {
            actionData.val = actionData.val.systemHelperType + '-' + actionData.val.id
          }
        })
        break
      case APPROVAL_ACTION_TYPE.NOTIFY:
        action.actionData = action.customNotificationText || ''
        action.customNotificationText = undefined
        break
      case APPROVAL_ACTION_TYPE.COPY:
        action.actionData = { id: action.copyToFsiId, name: action.copyToFsiName }
        action.copyToFsiId = undefined
        action.copyToFsiName = undefined
        break
      case APPROVAL_ACTION_TYPE.MOVE:
        action.actionData = { id: action.moveToFsiId, name: action.moveToFsiName }
        action.moveToFsiId = undefined
        action.moveToFsiName = undefined
        break
    }

    _.forEach(action.notifies, (stepItem) => {
      action.usersGroups.push(stepItem.group ? 'GROUP-' + stepItem.group.id : 'USER-' + stepItem.user.id)
    })

    action.notifies = undefined
    action['@type'] = undefined
  })

  _.forEach([WF_TPL_STEP.trainees, WF_TPL_STEP.rejectionReviewers], (key) => {
    if (tpl[key]) {
      tpl[key].usersGroups = []
      if (tpl[key].steps[0]) {
        _.forEach(tpl[key].steps[0].taskTeams, (stepItem) => {
          transformedTpl[key].usersGroups.push(
            stepItem.group ? 'GROUP-' + stepItem.group.id : 'USER-' + stepItem.user.id
          )
        })
      }
      tpl[key].steps = undefined
    }
  })

  return transformedTpl
}
